/**
 * Application state store — TASK 7.3.
 *
 * This is the vanilla-JS replacement for the mock's `DCLogic` state object +
 * `setState`/`renderVals` model (mock/SteamyMusic.dc.html L841-848, L940-1006).
 * The mock's model was: one plain object, a `setState(patch|fn)` that merges
 * and re-renders, and a `renderVals()` that derives view values. Here:
 *
 *   - the plain object survives  -> {@link getState};
 *   - `setState(patch|fn)`       -> {@link setState} (same merge semantics);
 *   - `renderVals()` re-render   -> {@link subscribe} listeners.
 *
 * ── WHAT DELIBERATELY DID **NOT** SURVIVE (dev-plan TASK 7.3 Step 1b, D4/W1a)
 * The mock's SIMULATION LAYER is gone. There is no `flags` object, no
 * `authed`/`role`/`offline`/`fail`/`playing` in-memory sim toggles, no
 * `_applyFlags()` query-param overrides, no fake-latency `setTimeout`s and no
 * `_optim()` mutation-rollback. What replaces them:
 *
 *   sim `flags.authed` / `flags.role` -> `state.session` (REAL session, set by
 *                                        the auth flow in TASK 7.4)
 *   sim data from `steamy-data.js`    -> `state.catalog` (REAL reads, set by
 *                                        `src/api/api-client.ts` in TASK 7.3's
 *                                        second half)
 *   sim `flags.offline`               -> not modelled here; real connectivity
 *                                        lands with the service worker (TASK 10.1)
 *   sim `flags.fail` + `_optim()`     -> deleted outright; real mutations get
 *                                        their errors from the API envelope
 *
 * ── SEAM (see ./README.md) ────────────────────────────────────────────────
 * This module is the SINGLE integration point for the next agent. The
 * api-client writes through {@link setState}; the telemetry client registers
 * itself through {@link setTelemetryEmitter}. Neither is imported here — the
 * store has no dependencies at all, by design, so it can be unit-tested and
 * so a store import never drags the network layer into the first-paint bundle.
 *
 * @module
 */

/** @typedef {'admin'|'member'} Role */

/**
 * @typedef {object} SessionUser
 * @property {string} email
 * @property {string} name
 * @property {string} initial single-character avatar glyph (mock header L98)
 */

/**
 * @typedef {object} Session
 * @property {boolean} authenticated
 * @property {Role|null} role
 * @property {SessionUser|null} user
 * @property {string|null} userId the signed-in user's id, straight from
 *   `GET /api/me`. NOT for display (there is nothing to display — see
 *   `SessionUser`); it is the OWNER STAMP that binds locally-parked
 *   PlayEvents to the session that captured them, so a device that changes
 *   user cannot merge one person's listening history into another's account
 *   (`lib/offline-store.ts#PLAY_EVENT_OWNER_FIELD`, Security W4).
 */

/**
 * @typedef {object} Track
 * @property {string} id
 * @property {string} title
 * @property {string} artist
 * @property {string} album
 * @property {string} albumId
 * @property {string|null} [coverRef] the album's cover ref, carried so the
 *   mini/full player can show the REAL cover art (`covers/…` → `/api/art/:albumId`)
 *   instead of only the generated gradient. `null`/absent keeps the gradient —
 *   the same has-real-art rule the album tiles use (`format.js#albumArtUrl`).
 * @property {number} dur seconds
 * @property {string} [format] catalog container (`mp3`/`m4a`/`flac`/`ogg`) —
 *   TASK 8.2 feeds it to `lib/codec-detect.ts` before a stream is requested
 * @property {boolean} [formatSupported] the SERVER's verified-matrix baseline;
 *   the client's runtime probe overrides it either way (AC 5.7)
 */

/**
 * The full player's status line — ui-contract §10.6 "Full player banners",
 * where each `kind` has one distinct message and its own Retry affordance.
 * Kept in the store (not inside the engine) because the mini player, the full
 * player and the album surface all read it.
 *
 * @typedef {object} PlaybackBanner
 * @property {'buffering'|'error'|'format-unsupported'|'no-longer-available'
 *   |'rate-limited'|'stream-limit'|'egress-quota'|'playback-halted'} kind
 * @property {string} message
 * @property {boolean} retry whether the banner offers a Retry control
 * @property {boolean} [retryDisabled] the Retry control is PRESENT but refused,
 *   because a 429's `Retry-After` window has not elapsed. Rendering it disabled
 *   rather than removing it keeps the banner's height stable and tells the user
 *   why nothing happens; `retryAfterSec` carries the remaining wait.
 * @property {number} [retryAfterSec] honoured `Retry-After` for the 429 family
 */

/**
 * @typedef {object} Playback
 * @property {Track|null} current
 * @property {Track[]} queue
 * @property {Track[]} past
 * @property {boolean} playing
 * @property {number} pos seconds elapsed
 * @property {number} dur seconds total
 * @property {boolean} buffering
 * @property {boolean} shuffle
 * @property {'off'|'all'|'one'} repeat
 * @property {PlaybackBanner|null} banner
 */

/**
 * @typedef {object} RouteState
 * @property {string} path normalised path inside the hash (`/albums/alb-x`)
 * @property {string} query raw query string inside the hash (no leading `?`)
 * @property {string} hash the full hash payload (`/albums/alb-x?state=error`)
 * @property {string} routeId id from ./router.js's ROUTES table
 */

/**
 * One row of the mock's Downloads list (`steamy-data.js` L122).
 *
 * TYPED, NOT `unknown` — but read the scope note. The DURABLE download state
 * lives in IndexedDB and is read through `lib/download-manager.ts#snapshot`
 * (TASK 10.1), because it has to survive a reload and be readable with no
 * network; `/downloads` renders from there and never from this slice. What
 * remains here is the DEV parity fixture's snapshot, which
 * `dev/mock-parity-fixture.js` writes so `pnpm dev` and the fidelity crawl see
 * a populated shell without a real download having been performed. It is
 * typed rather than deleted so the fixture keeps type-checking against a
 * declared shape instead of an `unknown[]` that admits anything.
 *
 * @typedef {object} DownloadFixtureRow
 * @property {string} id
 * @property {'Album'|'Playlist'|'Track'} type
 * @property {string} title
 * @property {string} sub secondary line ("Radiohead · 10 tracks")
 * @property {number} mb size on device, megabytes
 * @property {'queued'|'downloading'|'resuming'|'done'|'failed'} status
 * @property {number} [pct] 0-100 while `downloading`
 */

/**
 * @typedef {object} Catalog
 * @property {unknown[]} albums
 * @property {unknown[]} artists
 * @property {unknown[]} genres
 * @property {unknown[]} playlists
 * @property {DownloadFixtureRow[]} downloads DEV fixture only — see the typedef
 * @property {unknown[]} history
 * @property {{ tracks: string[], albums: string[] }} favorites
 */

/**
 * @typedef {object} AppState
 * @property {boolean} ready first data load finished (mock's `state.ready`)
 * @property {Session} session
 * @property {RouteState} route
 * @property {Playback} playback
 * @property {Catalog} catalog
 * @property {string} live last polite-announcement string (mock's `state.live`)
 */

/**
 * The zero value of the store. Exported so tests (and the api-client) can
 * reason about the shape without reaching into module state.
 *
 * @returns {AppState}
 */
export function initialState() {
  return {
    ready: false,
    session: { authenticated: false, role: null, user: null, userId: null },
    route: { path: '/', query: '', hash: '/', routeId: 'home' },
    playback: {
      current: null,
      queue: [],
      past: [],
      playing: false,
      pos: 0,
      dur: 0,
      buffering: false,
      shuffle: false,
      repeat: 'off',
      banner: null,
    },
    catalog: {
      albums: [],
      artists: [],
      genres: [],
      playlists: [],
      downloads: [],
      history: [],
      favorites: { tracks: [], albums: [] },
    },
    live: '',
  };
}

/** @type {AppState} */
let state = initialState();

/** @type {Set<(next: AppState, previous: AppState) => void>} */
const listeners = new Set();

/**
 * Reusable dispatch buffer.
 *
 * `setState` runs on every `timeupdate` — about four times a second for the
 * whole time audio plays — and it used to allocate a fresh `[...listeners]`
 * array on each one purely to be safe against a listener unsubscribing mid
 * notify. The COPY is still necessary (mutating a `Set` while iterating it is
 * how a subscriber that removes itself skips its neighbour), but a single
 * buffer reused across dispatches gets the same safety with no allocation.
 * `setState` is never re-entrant in this app — a listener that called it would
 * be a bug regardless — and the guard below makes that explicit rather than
 * silently corrupting the buffer if it ever happened.
 *
 * @type {Array<(next: AppState, previous: AppState) => void>}
 */
let dispatchBuffer = [];
let dispatching = false;

/**
 * Current state. Treat the returned object as read-only — mutate through
 * {@link setState} so subscribers actually fire.
 *
 * @returns {AppState}
 */
export function getState() {
  return state;
}

/**
 * Shallow-merges a patch (or the result of an updater function) into the
 * store and notifies every subscriber — the mock's `setState` semantics,
 * including the `setState(s => ({...}))` updater form it used for queue and
 * toast mutations.
 *
 * @param {Partial<AppState>|((current: AppState) => Partial<AppState>)} patch
 * @returns {AppState} the new state
 */
export function setState(patch) {
  const resolved = typeof patch === 'function' ? patch(state) : patch;
  const previous = state;

  /*
   * NO-OP SHORT-CIRCUIT. Every top-level key the patch names is compared by
   * REFERENCE against what the store already holds — which is the right test
   * for this store, because every writer produces a new object for anything it
   * changes (`patch()` in the audio engine spreads `state.playback`, the
   * favourites toggle spreads `state.catalog`). So reference-equal means
   * "nothing changed" with no deep walk and no false negatives.
   *
   * It matters because the store fans out to the shell, the mini-player, the
   * Media Session bridge and every mounted view, at ~4 Hz while audio plays.
   * A `setState({ready: true})` that re-asserts the value it already had used
   * to repaint all of them.
   */
  let changed = false;
  for (const key of Object.keys(resolved)) {
    if (!Object.is(state[key], resolved[key])) {
      changed = true;
      break;
    }
  }
  if (!changed) return state;

  state = { ...state, ...resolved };

  // Re-entrant `setState` from inside a listener would clobber the shared
  // buffer mid-iteration; fall back to a private copy rather than corrupt it.
  if (dispatching) {
    for (const listener of [...listeners]) listener(state, previous);
    return state;
  }
  dispatching = true;
  try {
    dispatchBuffer.length = 0;
    for (const listener of listeners) dispatchBuffer.push(listener);
    for (const listener of dispatchBuffer) listener(state, previous);
  } finally {
    dispatchBuffer.length = 0;
    dispatching = false;
  }
  return state;
}

/**
 * Registers a listener, returning its unsubscribe function.
 *
 * @param {(next: AppState, previous: AppState) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Restores the zero value and drops every listener. Test-only affordance —
 * the app itself has exactly one store lifetime (the page load).
 *
 * @returns {AppState}
 */
export function resetStore() {
  state = initialState();
  listeners.clear();
  dispatchBuffer = [];
  dispatching = false;
  return state;
}

/* ── Telemetry seam (dev-plan TASK 7.2 Step 7 / W17) ───────────────────────
 *
 * The shell emits `session_started` on session gain. It must NOT import
 * `src/lib/telemetry-client.ts` directly: that module batches + POSTs to
 * /api/telemetry and belongs to the next agent's slice. So the shell calls an
 * INJECTED emitter with the interface below, and the default is a no-op.
 *
 * Next agent: `telemetry-client.ts` calls
 *   setTelemetryEmitter({ emit: (event, payload) => queue.push({event, ...payload}) })
 * once at startup. Nothing else changes.
 */

/**
 * @typedef {object} TelemetryEmitter
 * @property {(event: string, payload: Record<string, string|number|boolean>) => void} emit
 *   Fire-and-forget. MUST NOT throw and MUST NOT block the caller — the shell
 *   calls it during mount.
 */

/** The default emitter: swallows everything. Never null, so callers need no guard. */
export const noopTelemetryEmitter = Object.freeze({
  emit() {
    /* no-op until telemetry-client.ts registers itself (TASK 7.3) */
  },
});

/** @type {TelemetryEmitter} */
let telemetryEmitter = noopTelemetryEmitter;

/**
 * Installs the process-wide telemetry emitter.
 *
 * @param {TelemetryEmitter|null} emitter pass null to restore the no-op
 * @returns {TelemetryEmitter} the emitter that was previously installed
 */
export function setTelemetryEmitter(emitter) {
  const previous = telemetryEmitter;
  telemetryEmitter = emitter ?? noopTelemetryEmitter;
  return previous;
}

/**
 * The currently installed emitter.
 *
 * @returns {TelemetryEmitter}
 */
export function getTelemetryEmitter() {
  return telemetryEmitter;
}
